import React, { useId } from "react";
import UploadedAttachmentList from "../UploadedAttachmentList";
import ReviewedTaskLockNotice from "../ReviewedTaskLockNotice";
import "../../styles/CustomTaskAttachmentSection.css";

const CustomTaskAttachmentSection = ({
  apiBase,
  attachmentTypes = [],
  selectedAttachmentType,
  onAttachmentTypeChange,
  pendingFiles,
  uploadedFiles,
  canEdit,
  canDeleteSavedAttachments = canEdit,
  isSaving,
  isReviewed,
  deletingFileId,
  onSelectFiles,
  onRemovePendingFile,
  onDeleteSavedAttachment,
  onSave,
  showSaveButton = true,
  beforeSaveContent = null,
  hideFilePicker = false,
  hideAttachmentSummary = false,
  hideSaveDivider = false,
}) => {
  const fileInputId = useId();
  const fileInputDisabled =
    !canEdit ||
    isSaving ||
    (attachmentTypes.length > 0 && !selectedAttachmentType);
  const selectedFileNames = pendingFiles.length
    ? pendingFiles.map((entry) => entry.file.name).join(", ")
    : "No files selected";

  return (
    <div data-print-attachments-section>
    {!hideAttachmentSummary && <>
    <hr className="my-4" />

    <h5>Attachments</h5>

    <form onSubmit={onSave}>
      {!hideFilePicker && attachmentTypes.length > 0 && (
        <div className="mb-3">
          <label className="form-label">Attachment Type</label>
          <select
            className="form-control"
            value={selectedAttachmentType}
            disabled={!canEdit || isSaving}
            onChange={(event) => onAttachmentTypeChange(event.target.value)}
          >
            <option value="">Select an attachment type</option>
            {attachmentTypes.map((attachmentType) => (
              <option key={attachmentType} value={attachmentType}>
                {attachmentType}
              </option>
            ))}
          </select>
        </div>
      )}

      {!hideFilePicker && <div className="mb-3">
        <label className="form-label">Select File</label>
        <div className={`custom-task-file-picker${fileInputDisabled ? " is-disabled" : ""}`}>
          <input
            id={fileInputId}
            type="file"
            className="custom-task-file-input"
            hidden
            accept=".pdf,.jpg,.jpeg,.png,.msg"
            multiple
            disabled={fileInputDisabled}
            onChange={onSelectFiles}
          />
          <label
            className="custom-task-file-button"
            htmlFor={fileInputId}
            aria-disabled={fileInputDisabled}
          >
            Choose Files
          </label>
          <span className="custom-task-file-name">
            {selectedFileNames}
          </span>
        </div>
      </div>}

      {pendingFiles.length > 0 && (
        <div className="mt-4 p-3 bg-light rounded border">
          <div className="text-muted mb-2">Files Ready To Save</div>
          <ul className="list-unstyled mb-0">
            {pendingFiles.map((pendingEntry, index) => (
              <li
                key={`${pendingEntry.file.name}_${pendingEntry.file.size}_${pendingEntry.file.lastModified}_${pendingEntry.attachmentType}_${index}`}
                className="d-flex justify-content-between align-items-center py-1"
              >
                <span>
                  {pendingEntry.file.name}
                  {pendingEntry.attachmentType
                    ? ` - ${pendingEntry.attachmentType}`
                    : ""}
                </span>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  data-delete-permission-exempt="true"
                  onClick={() => onRemovePendingFile(pendingEntry)}
                  disabled={!canEdit || isSaving}
                  title="Delete"
                >
                  <i className="bi bi-trash"></i>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </form>

    <UploadedAttachmentList
      title="Uploaded Files"
      files={uploadedFiles}
      getFileKey={(file) => file.ta_id}
      getFileName={(file) => file.ta_original_name}
      getFileMeta={(file) => file.ta_type || null}
      getFileViewUrl={(file) =>
        `${apiBase}/api/custom-tasks/attachment-file/${file.ta_id}?mode=view`
      }
      getFileDownloadUrl={(file) =>
        `${apiBase}/api/custom-tasks/attachment-file/${file.ta_id}?mode=download`
      }
      canDelete={canDeleteSavedAttachments}
      deletePermissionExempt
      onDeleteFile={onDeleteSavedAttachment}
      deletingFileId={deletingFileId}
    />
    </>}

    {isReviewed && <ReviewedTaskLockNotice className="mt-3" />}

    {!hideSaveDivider && (beforeSaveContent || showSaveButton) && (
      <hr className="my-4" />
    )}

    {beforeSaveContent}

    {showSaveButton && (
      <div className="d-flex justify-content-end mt-3">
        <button
          type="button"
          className="btn btn-primary"
          onClick={onSave}
          disabled={!canEdit || isSaving}
        >
          {isSaving ? "Saving..." : "Save"}
        </button>
      </div>
    )}
    </div>
  );
};

export default CustomTaskAttachmentSection;
